import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LaunchIcon from '@mui/icons-material/Launch';
import { StatusChip } from '../../../../components/StatusChip';
import { DeductionStatusMap } from '../constants';
import { DeductionStatus, DeductionField, ProvisioningStatus } from '../../../../types';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from 'react';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import TruncatedText from '../../../../components/TruncatedText';
import { ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { getAccountingSyncStatusColor } from '../../../../helpers/getAccountingSyncStatusColor';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { getDeductionRecord } from '../../../../api/paymentsApi/deductionsApi';
import {
  getVendorPaymentMethodDetails,
  type VendorPaymentMethodDetails,
} from '../../../../api/vendorApi/vendorAccountsApi';
import {
  getVendorDefaultPaymentAccountId,
  getVendorPaymentAccountOptions,
} from '../../../../helpers/vendorPaymentAccounts';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme, { isLongData }) => ({
  summaryBox: {
    background: '#35394B',
    border: '1.5px solid #4A4E5D',
    borderRadius: '12px',
    padding: '20px 32px 20px 24px',
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  summaryStatusChipProcessing: {
    background: 'rgba(255, 193, 7, 0.08)',
    border: '1px solid #FFC107',
    borderRadius: '16px',
    padding: '2px 12px',
    color: '#FFC107',
    fontSize: 13,
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  summaryStatusChipFailed: {
    background: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid #F44336',
    borderRadius: '16px',
    padding: '2px 12px',
    color: '#F44336',
    fontSize: 13,
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  accordionRoot: {
    backgroundColor: 'transparent',
    borderRadius: '16px',
    // border: `1px solid ${theme.palette.secondary.dark}`,
    marginTop: theme.spacing(2),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Poppins',
  },
  tableWrapper: {
    overflow: 'hidden',
    background: theme.palette.primary.light,
    borderRadius: '8px',
    fontFamily: 'Poppins',
  },
  tableTitle: {
    fontWeight: 600,
    fontSize: '15px',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    fontFamily: 'Poppins',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    boxSizing: 'border-box',
    borderRadius: '8px',
    overflowX: 'auto',
    overflowY: 'visible',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Poppins',
    minHeight: isLongData ? '300px' : 'inherit',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },

  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      whiteSpace: 'nowrap',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Poppins',
      fontSize: '14px',
      verticalAlign: 'top',
      padding: '16px 12px 16px 8px',
    },
  },
  labelText: {
    fontSize: '12px !important',
    fontFamily: 'Poppins !important',
    color: '#FFFFFFB3 !important',
    fontWeight: '200 !important',
    paddingTop: '2px !important',
  },
  columnVendor: {
    width: '210px',
    minWidth: '210px !important',
    maxWidth: '210px !important',
    whiteSpace: 'normal',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
  },
  columnAmount: {
    width: '110px',
    minWidth: '110px !important',
    maxWidth: '110px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnIntent: {
    width: '90px',
    minWidth: '90px !important',
    maxWidth: '90px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnPaymentMethod: {
    width: '130px',
    minWidth: '130px !important',
    maxWidth: '130px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  columnPayAccount: {
    width: '320px',
    minWidth: '320px !important',
    maxWidth: '320px !important',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
  },
  columnStatus: {
    width: '170px',
    minWidth: '170px !important',
    maxWidth: '170px !important',
    whiteSpace: 'normal',
    overflow: 'visible',
    textOverflow: 'clip',
  },
  columnAction: {
    width: '96px',
    minWidth: '96px !important',
    maxWidth: '96px !important',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  statusAccountingIcons: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  stopDialogPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    minWidth: '420px',
  },
  retryDialogPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    width: 'min(560px, calc(100vw - 32px))',
    minWidth: '0',
    maxWidth: 'calc(100vw - 32px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
  },
  stopDialogContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '12px',
    },
    '& .MuiDialogTitle-root': {
      color: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    },
    '& .MuiDialogContent-root': {
      backgroundColor: theme.palette.primary.light,
    },
    '& .MuiDialogActions-root': {
      backgroundColor: theme.palette.primary.light,
      borderTop: `1px solid ${theme.palette.secondary.dark}`,
    },
  },
  stopDialogTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
  },
  stopDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(2)} !important`,
  },
  retryDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(2)} !important`,
    overflowY: 'visible',
    overflowX: 'hidden',
    width: '100%',
    boxSizing: 'border-box',
  },
  stopDialogActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
  },
  retryDialogActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
    flexWrap: 'nowrap',
    width: '100%',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '& .MuiButton-root': {
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    '& > :not(style) ~ :not(style)': {
      marginLeft: '0 !important',
    },
    '@media (max-width: 640px)': {
      flexWrap: 'wrap',
    },
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontFamily: 'Inter',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.info.main,
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      '& fieldset': {
        borderColor: theme.palette.secondary.dark,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.info.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.info.main,
      },
    },
  },
  menuItem: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
  },
  helperText: {
    color: '#FF8663',
    fontFamily: 'Inter',
    fontSize: '12px',
    marginTop: theme.spacing(-1),
  },
  secondaryButton: {
    color: theme.palette.common.white,
    borderColor: theme.palette.secondary.dark,
    minHeight: '34px',
    height: '34px',
    padding: '6px 14px',
    borderRadius: '6px',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
  },
  primaryButton: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    minHeight: '34px',
    height: '34px',
    padding: '6px 14px',
    borderRadius: '6px',
    textTransform: 'none',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    },
  },
}));

const STOP_CHECK_REASON_MAP: Record<string, string> = {
  'Sent to the wrong address': 'INCORRECT_DESTINATION',
  'Check has not arrived / appears lost': 'LOST_CHECK',
  'Incorrect payee name': 'INCORRECTLY_CREATED',
  'Incorrect amount': 'INCORRECT_AMOUNT',
  'I want a different payout method': 'ON_USER_REQUEST',
  'Other': 'OTHERS',
};

interface SettlementsDeductionsTableProps {
  fields: DeductionField[];
  onRetryVendorPayment?: (deductionId: number | string) => void;
  onRetryVendorPaymentWithUpdate?: (
    deductionId: number | string,
    paymentMethod: string,
    vendorPaymentAccountId?: number | null,
    vendorPaymentAccountName?: string | null,
  ) => void;
  onStopDeductionTransaction?: (
    deductionId: number | string,
    reason: string,
    additionalComments: string,
  ) => void;
  handleMenuOptionClick: (option: string, index: number, fieldIndex: number) => void;
}

const DetailsDeductionTable = ({
  fields,
  onRetryVendorPayment,
  onRetryVendorPaymentWithUpdate,
  onStopDeductionTransaction,
  handleMenuOptionClick,
}: SettlementsDeductionsTableProps) => {
  const deductionsFields = fields;
  let isLongData;
  const styles = useStyles({ isLongData });

  const [liensPage, setLiensPage] = useState(0);
  const [liensRowsPerPage, setLiensRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [expensesPage, setExpensesPage] = useState(0);
  const [expensesRowsPerPage, setExpensesRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
  const [serviceProviderPage, setServiceProviderPage] = useState(0);
  const [serviceProviderRowsPerPage, setServiceProviderRowsPerPage] = useState(
    ROWS_PER_PAGE_OPTIONS[0],
  );
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  const [selectedStopReasonLabel, setSelectedStopReasonLabel] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [showStopReasonError, setShowStopReasonError] = useState(false);
  const [showAdditionalCommentsError, setShowAdditionalCommentsError] = useState(false);
  const [selectedDeductionId, setSelectedDeductionId] = useState<number | string | null>(null);
  const [isRetryOptionsDialogOpen, setIsRetryOptionsDialogOpen] = useState(false);
  const [retryDialogMode, setRetryDialogMode] = useState<'options' | 'differentOption'>('options');
  const [selectedRetryDeduction, setSelectedRetryDeduction] = useState<DeductionField | null>(null);
  const [isRetryOverrideLoading, setIsRetryOverrideLoading] = useState(false);
  const [retryMethodOptions, setRetryMethodOptions] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [retryPaymentMethod, setRetryPaymentMethod] = useState('');
  const [retryAccountOptions, setRetryAccountOptions] = useState<Array<{ id: string; label: string }>>(
    [],
  );
  const [retryPaymentAccountId, setRetryPaymentAccountId] = useState('');
  const [retryVendorPaymentDetails, setRetryVendorPaymentDetails] =
    useState<VendorPaymentMethodDetails | null>(null);
  const [retryOverrideError, setRetryOverrideError] = useState('');

  const stopReasonOptions = Object.keys(STOP_CHECK_REASON_MAP);
  const darkSelectMenuProps = {
    PaperProps: {
      sx: {
        backgroundColor: 'primary.light',
        border: (theme: Theme) => `1px solid ${theme.palette.secondary.dark}`,
        '& .MuiMenuItem-root': {
          color: 'common.white',
        },
      },
    },
  };

  const handleOpenStopDialog = (deductionId: number | string) => {
    setSelectedDeductionId(deductionId);
    setIsStopDialogOpen(true);
  };

  const handleCloseStopDialog = () => {
    setIsStopDialogOpen(false);
    setSelectedDeductionId(null);
    setSelectedStopReasonLabel('');
    setAdditionalComments('');
    setShowStopReasonError(false);
    setShowAdditionalCommentsError(false);
  };

  const handleSubmitStopTransaction = () => {
    const selectedReasonCode = STOP_CHECK_REASON_MAP[selectedStopReasonLabel];

    if (!selectedReasonCode) {
      setShowStopReasonError(true);
      return;
    }

    if (selectedStopReasonLabel === 'Other' && !additionalComments.trim()) {
      setShowAdditionalCommentsError(true);
      return;
    }

    if (!selectedDeductionId || !onStopDeductionTransaction) {
      handleCloseStopDialog();
      return;
    }

    onStopDeductionTransaction(
      selectedDeductionId,
      selectedReasonCode,
      additionalComments.trim(),
    );
    handleCloseStopDialog();
  };

  const handleOpenRetryOptionsDialog = (deduction: DeductionField) => {
    setSelectedRetryDeduction(deduction);
    setRetryDialogMode('options');
    setIsRetryOptionsDialogOpen(true);
  };

  const handleCloseRetryOptionsDialog = () => {
    setIsRetryOptionsDialogOpen(false);
    setRetryDialogMode('options');
    setSelectedRetryDeduction(null);
    setRetryOverrideError('');
    setRetryMethodOptions([]);
    setRetryPaymentMethod('');
    setRetryAccountOptions([]);
    setRetryPaymentAccountId('');
    setRetryVendorPaymentDetails(null);
  };

  const handleRetrySameData = () => {
    if (!selectedRetryDeduction || !onRetryVendorPayment) {
      handleCloseRetryOptionsDialog();
      return;
    }

    const validateAndRetrySameData = async () => {
      setIsRetryOverrideLoading(true);
      setRetryOverrideError('');

      try {
        const deductionResponse = await getDeductionRecord(String(selectedRetryDeduction.id));
        const vendorId = deductionResponse.data?.vendor?.id;

        if (!vendorId) {
          setRetryOverrideError('Vendor information is not available for this deduction.');
          return;
        }

        const paymentDetailsResponse = await getVendorPaymentMethodDetails(vendorId);
        const vendorDetails = paymentDetailsResponse.data;
        setRetryVendorPaymentDetails(vendorDetails);

        const paymentMethod = (selectedRetryDeduction.paymentMethod || '').toUpperCase();
        const requiresAccount = paymentMethod === 'ACH' || paymentMethod === 'CHECK';

        if (requiresAccount) {
          const paymentAccountId = selectedRetryDeduction.paymentAccountId
            ? String(selectedRetryDeduction.paymentAccountId)
            : '';

          if (!paymentAccountId) {
            setRetryOverrideError('Payment account is required to retry with the same option.');
            return;
          }

          const selectedAccount =
            paymentMethod === 'ACH'
              ? vendorDetails.achAccounts?.find((account) => String(account.id) === paymentAccountId)
              : vendorDetails.checkAccounts?.find(
                  (account) => String(account.id) === paymentAccountId,
                );

          if (!selectedAccount) {
            setRetryOverrideError('The selected payment account is no longer available.');
            return;
          }

          if (selectedAccount.provisionStatus !== ProvisioningStatus.Completed) {
            setRetryOverrideError(
              'Selected payment account is not fully provisioned. Please choose an account with Completed status.',
            );
            return;
          }
        }

        onRetryVendorPayment(selectedRetryDeduction.id);
        handleCloseRetryOptionsDialog();
      } catch {
        setRetryOverrideError('Unable to validate vendor payment account for retry.');
      } finally {
        setIsRetryOverrideLoading(false);
      }
    };

    validateAndRetrySameData();
  };

  const handleRetryOtherMethod = async () => {
    if (!selectedRetryDeduction || !onRetryVendorPaymentWithUpdate) {
      handleCloseRetryOptionsDialog();
      return;
    }

    setIsRetryOverrideLoading(true);
    setRetryOverrideError('');

    try {
      const deductionResponse = await getDeductionRecord(String(selectedRetryDeduction.id));
      const vendorId = deductionResponse.data?.vendor?.id;

      if (!vendorId) {
        setRetryOverrideError('Vendor information is not available for this deduction.');
        return;
      }

      const paymentDetailsResponse = await getVendorPaymentMethodDetails(vendorId);
      const vendorDetails = paymentDetailsResponse.data;
      setRetryVendorPaymentDetails(vendorDetails);

      const methodOptions = (vendorDetails.activePaymentMethod || [])
        .filter((method) => ['ACH', 'CHECK', 'V'].includes(method))
        .map((method) => ({ id: method, label: method }));

      if (!methodOptions.length) {
        setRetryOverrideError('No active vendor payment methods are available for retry.');
        return;
      }

      setRetryMethodOptions(methodOptions);

      const defaultMethod =
        (selectedRetryDeduction.paymentMethod &&
          methodOptions.some((option) => option.id === selectedRetryDeduction.paymentMethod)
          ? selectedRetryDeduction.paymentMethod
          : methodOptions[0]?.id) || '';

      setRetryPaymentMethod(defaultMethod);
      setRetryDialogMode('differentOption');
    } catch {
      setRetryOverrideError('Unable to load vendor payment methods for retry.');
    } finally {
      setIsRetryOverrideLoading(false);
    }
  };

  const getFormattedAccountOptions = (details: VendorPaymentMethodDetails, method: string) => {
    return getVendorPaymentAccountOptions(details, method).map((item) => {
      const subtitleWithoutAccountType =
        item.paymentMethod === 'ACH' ? item.subtitle.split(' ').slice(-1).join(' ') : item.subtitle;

      return {
        id: item.id,
        label: subtitleWithoutAccountType ? `${item.label} - ${subtitleWithoutAccountType}` : item.label,
      };
    });
  };

  const handleRetryWithUpdatedData = () => {
    if (!selectedRetryDeduction || !onRetryVendorPaymentWithUpdate) {
      handleCloseRetryOptionsDialog();
      return;
    }

    if (!retryPaymentMethod) {
      setRetryOverrideError('Payment method is required.');
      return;
    }

    const requiresAccount = retryPaymentMethod === 'ACH' || retryPaymentMethod === 'CHECK';
    if (requiresAccount && !retryPaymentAccountId) {
      setRetryOverrideError('Payment account is required.');
      return;
    }

    if (requiresAccount) {
      const selectedAccount =
        retryPaymentMethod === 'ACH'
          ? retryVendorPaymentDetails?.achAccounts?.find(
              (account) => String(account.id) === retryPaymentAccountId,
            )
          : retryVendorPaymentDetails?.checkAccounts?.find(
              (account) => String(account.id) === retryPaymentAccountId,
            );

      if (!selectedAccount) {
        setRetryOverrideError('The selected payment account is no longer available.');
        return;
      }

      if (selectedAccount.provisionStatus !== ProvisioningStatus.Completed) {
        setRetryOverrideError(
          'Selected payment account is not fully provisioned. Please choose an account with Completed status.',
        );
        return;
      }
    }

    const selectedAccountOption = retryAccountOptions.find((option) => option.id === retryPaymentAccountId);

    onRetryVendorPaymentWithUpdate(
      selectedRetryDeduction.id,
      retryPaymentMethod,
      retryPaymentAccountId ? Number(retryPaymentAccountId) : null,
      selectedAccountOption?.label || null,
    );

    handleCloseRetryOptionsDialog();
  };

  useEffect(() => {
    if (!retryVendorPaymentDetails || !retryPaymentMethod) {
      setRetryAccountOptions([]);
      setRetryPaymentAccountId('');
      return;
    }

    const accountOptions = getFormattedAccountOptions(retryVendorPaymentDetails, retryPaymentMethod);
    setRetryAccountOptions(accountOptions);

    setRetryPaymentAccountId((previous) => {
      if (accountOptions.some((option) => option.id === previous)) {
        return previous;
      }

      const currentAccountId = selectedRetryDeduction?.paymentAccountId
        ? String(selectedRetryDeduction.paymentAccountId)
        : '';
      if (accountOptions.some((option) => option.id === currentAccountId)) {
        return currentAccountId;
      }

      return getVendorDefaultPaymentAccountId(retryVendorPaymentDetails, retryPaymentMethod) || '';
    });
  }, [retryVendorPaymentDetails, retryPaymentMethod, selectedRetryDeduction?.paymentAccountId]);

  const renderTable = (title: string) => {
    const isLien = title === 'Lien';
    const isServiceProvider = title === 'Service Provider';
    const isExpense = title === 'Advance';
    const page = isLien ? liensPage : isServiceProvider ? serviceProviderPage : expensesPage;
    const rowsPerPage = isLien
      ? liensRowsPerPage
      : isServiceProvider
      ? serviceProviderRowsPerPage
      : expensesRowsPerPage;
    const setPage = isLien
      ? setLiensPage
      : isServiceProvider
      ? setServiceProviderPage
      : setExpensesPage;
    const setRowsPerPage = isLien
      ? setLiensRowsPerPage
      : isServiceProvider
      ? setServiceProviderRowsPerPage
      : setExpensesRowsPerPage;

    const dedType =
      title === 'Lien' ? 'Lien' : title === 'Service Provider' ? 'SERVICE_PROVIDER' : 'Advance';
    // Filter deductions by type
    const filteredIndices = fields
      .map((field, idx) => ({ field, idx }))
      .filter(({ idx }) => deductionsFields?.[idx]?.type === dedType)
      .map(({ idx }) => idx);

    const paginatedIndices = filteredIndices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
    isLongData = filteredIndices.length > 3;
    const tableMinWidth = isExpense ? 860 : 1180;

    return (
      <Box className={styles.classes.tableWrapper}>
        <Typography className={styles.classes.tableTitle}>
          {title === 'Lien'
            ? 'Liens'
            : title === 'Service Provider'
            ? 'Service Providers'
            : 'Expenses'}
        </Typography>
        <TableContainer
          className={styles.classes.tableContainer}
          sx={{ width: '100%' }}
          component={Paper}
          elevation={0}
        >
          <Table
            sx={{
              width: `max(100%, ${tableMinWidth}px)`,
              minWidth: tableMinWidth,
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
            }}
          >
            <TableHead className={styles.classes.tableHeaderCell}>
              <TableRow>
                <TableCell
                  key={title === 'Lien' || title === 'Service Provider' ? 'Vendor' : 'Description'}
                  className={styles.classes.columnVendor}
                >
                  {title === 'Lien' || title === 'Service Provider' ? 'Vendor' : 'Description'}
                </TableCell>
                <TableCell key={'Amount'} className={styles.classes.columnAmount}>{'Amount'}</TableCell>
                <TableCell key={'Intent'} className={styles.classes.columnIntent}>{'Intent'}</TableCell>
                <TableCell key={'PaymentMethod'} className={styles.classes.columnPaymentMethod}>{'Payment Method'}</TableCell>
                {!isExpense && <TableCell key={'PayAccount'} className={styles.classes.columnPayAccount}>{'PAY Account'}</TableCell>}
                <TableCell key={'Status'} className={styles.classes.columnStatus}>
                  {'Status'}
                </TableCell>
                <TableCell key={'Action'} className={styles.classes.columnAction}>
                  {'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={clsx(styles.classes.tableBodyCell)}>
              {paginatedIndices.length > 0 ? (
                paginatedIndices.map((idx) => {
                  const field = fields[idx];
                  const deduction = deductionsFields?.[idx] || {};
                  const isErrorStatus = deduction.status === DeductionStatus.Failed;
                  const statusChip = (
                    <StatusChip
                      label={DeductionStatusMap[deduction.status]?.label || deduction.status}
                      color={DeductionStatusMap[deduction.status]?.color || 'default'}
                    />
                  );
                  return (
                    <TableRow key={`${title}-${field.id}-${idx}`}>
                      <TableCell
                        sx={{
                          whiteSpace: 'normal',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          overflowWrap: 'break-word',
                          wordBreak: 'break-word',
                        }}
                        className={styles.classes.columnVendor}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          {title === 'Lien' || title === 'Service Provider' ? (
                            <TruncatedText text={deduction.vendorName || '-'} maxLength={30} />
                          ) : (
                            <TruncatedText
                              text={deduction.description || '-'}
                              maxLength={30}
                              sx={{ whiteSpace: 'pre-wrap' }}
                            />
                          )}
                          {deduction.accounting?.vendorLink != null && (
                            <Tooltip
                              title={`View Vendor in ${NETSUITE_LABEL}`}
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '140px',
                                  }),
                                },
                              }}
                            >
                              <LaunchIcon
                                sx={{
                                  cursor: 'pointer',
                                  color: 'common.white',
                                  fontSize: '20px',
                                  flexShrink: 0,
                                }}
                                onClick={() =>
                                  window.open(deduction.accounting?.vendorLink || '', '_blank')
                                }
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins !important' }} className={styles.classes.columnAmount}>
                        <Typography>{numberToUSDCurrency(deduction.amount) || '-'}</Typography>
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins !important' }} className={styles.classes.columnIntent}>
                        {deduction.amount !== 0 ? deduction.disposition || '-' : '-'}
                      </TableCell>

                      <TableCell sx={{ fontFamily: 'Poppins !important' }} className={styles.classes.columnPaymentMethod}>
                        {deduction.amount !== 0 ? deduction.paymentMethod || '-' : '-'}
                      </TableCell>

                      {!isExpense && (
                        <TableCell
                          sx={{
                            fontFamily: 'Poppins !important',
                            whiteSpace: 'normal',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                          className={styles.classes.columnPayAccount}
                        >
                          {deduction.amount !== 0 && ['PAY', 'Pay'].includes(deduction.disposition || '')
                            ? deduction.paymentAccountName || '-'
                            : '-'}
                        </TableCell>
                      )}

                      <TableCell
                        sx={{ fontFamily: 'Poppins !important' }}
                        className={styles.classes.columnStatus}
                      >
                        {deduction.amount !== 0 ? (
                          <Box className={styles.classes.statusCellContent}>
                            {isErrorStatus && deduction.paymentErrorMessage ? (
                              <Tooltip title={deduction.paymentErrorMessage} arrow>
                                <span>{statusChip}</span>
                              </Tooltip>
                            ) : (
                              statusChip
                            )}
                            <Box className={styles.classes.statusAccountingIcons}>
                              {(deduction.accounting?.billLink ||
                                deduction.accounting?.billError ||
                                deduction.accounting?.billSyncStatus) &&
                                (deduction.accounting?.billLink || deduction.accounting?.billError ? (
                                  <Tooltip
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: (theme) => ({
                                          backgroundColor: theme.palette.common.white,
                                          color: theme.palette.primary.light,
                                          borderRadius: '5px',
                                          boxShadow: '3px',
                                          fontFamily: 'Inter',
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          padding: '5px',
                                          maxWidth: '180px',
                                        }),
                                      },
                                    }}
                                    title={deduction.accounting?.billError || 'View Bill'}
                                  >
                                    <Box
                                      sx={{
                                        paddingTop: '5px',
                                        cursor: deduction.accounting?.billLink
                                          ? 'pointer'
                                          : 'default',
                                      }}
                                      onClick={
                                        deduction.accounting?.billLink
                                          ? () =>
                                              window.open(
                                                deduction.accounting?.billLink || '',
                                                '_blank',
                                              )
                                          : undefined
                                      }
                                    >
                                      <ReceiptIcon
                                        fontSize="small"
                                        sx={{
                                          color: getAccountingSyncStatusColor(
                                            deduction.accounting?.billSyncStatus,
                                          ),
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ paddingTop: '5px' }}>
                                    <ReceiptIcon
                                      fontSize="small"
                                      sx={{
                                        color: getAccountingSyncStatusColor(
                                          deduction.accounting?.billSyncStatus,
                                        ),
                                      }}
                                    />
                                  </Box>
                                ))}

                              {(deduction.accounting?.billPaymentLink ||
                                deduction.accounting?.billPaymentError ||
                                deduction.accounting?.billPaymentSyncStatus) &&
                                (deduction.accounting?.billPaymentLink ||
                                deduction.accounting?.billPaymentError ? (
                                  <Tooltip
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: (theme) => ({
                                          backgroundColor: theme.palette.common.white,
                                          color: theme.palette.primary.light,
                                          borderRadius: '5px',
                                          boxShadow: '3px',
                                          fontFamily: 'Inter',
                                          fontSize: '13px',
                                          fontWeight: 500,
                                          padding: '5px',
                                          maxWidth: '180px',
                                        }),
                                      },
                                    }}
                                    title={
                                      deduction.accounting?.billPaymentError || 'View Bill Payment'
                                    }
                                  >
                                    <Box
                                      sx={{
                                        paddingTop: '5px',
                                        cursor: deduction.accounting?.billPaymentLink
                                          ? 'pointer'
                                          : 'default',
                                      }}
                                      onClick={
                                        deduction.accounting?.billPaymentLink
                                          ? () =>
                                              window.open(
                                                deduction.accounting?.billPaymentLink || '',
                                                '_blank',
                                              )
                                          : undefined
                                      }
                                    >
                                      <PaymentsIcon
                                        fontSize="small"
                                        sx={{
                                          color: getAccountingSyncStatusColor(
                                            deduction.accounting?.billPaymentSyncStatus,
                                          ),
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Box sx={{ paddingTop: '5px' }}>
                                    <PaymentsIcon
                                      fontSize="small"
                                      sx={{
                                        color: getAccountingSyncStatusColor(
                                          deduction.accounting?.billPaymentSyncStatus,
                                        ),
                                      }}
                                    />
                                  </Box>
                                ))}
                            </Box>
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>

                      <TableCell className={styles.classes.columnAction}>
                        {/* <OptionsMenu page={'Details'} onClick={(option) => handleMenuOptionClick(option, Number(deductionsFields[idx].id), idx)} /> */}
                        <Box display="flex" gap={0.5} alignItems="center">
                          {deduction.hasDocument && (
                            <Tooltip
                              title="Download Invoice"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '140px',
                                  }),
                                },
                              }}
                            >
                              <FileDownloadIcon
                                sx={{
                                  color: 'common.white',
                                  marginLeft: 0,
                                }}
                                onClick={() =>
                                  handleMenuOptionClick(
                                    'Download Invoice',
                                    Number(deductionsFields[idx].id),
                                    idx,
                                  )
                                }
                                fontSize="small"
                              />
                            </Tooltip>
                          )}
                          {deduction.canRetryVendorPayment && onRetryVendorPayment && (
                            <RefreshIcon
                              onClick={() => handleOpenRetryOptionsDialog(field)}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                          {deduction.canStopDeductionTransaction && onStopDeductionTransaction && (
                            <Tooltip title="Stop Deduction Transaction" arrow>
                              <ReportGmailerrorredIcon
                                onClick={() => handleOpenStopDialog(field.id)}
                                style={{ cursor: 'pointer', color: '#FF8663' }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={isExpense ? 6 : 7}
                    align="center"
                    sx={{ textAlign: 'center !important' }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No{' '}
                      {title === 'Lien'
                        ? 'Lien'
                        : title === 'Service Provider'
                        ? 'Service Provider'
                        : 'Expense'}{' '}
                      records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePaginationLocalContainer
          count={filteredIndices.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0]));
            setPage(0);
          }}
          totalPages={Math.ceil(filteredIndices.length / rowsPerPage)}
        />
      </Box>
    );
  };

  return (
    <Accordion defaultExpanded className={styles.classes.accordionRoot}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        className={styles.classes.accordionSummary}
      >
        <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="8px">
            <Typography>Deductions</Typography>
            <Typography className={styles.classes.labelText}>
              Liens, Service Providers & Expenses
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails className={styles.classes.accordionDetails}>
        {renderTable('Lien')}
        {renderTable('Service Provider')}
        {renderTable('Advance')}

        <Dialog
          open={isStopDialogOpen}
          onClose={handleCloseStopDialog}
          className={styles.classes.stopDialogContainer}
          classes={{ paper: styles.classes.stopDialogPaper }}
        >
          <DialogTitle className={styles.classes.stopDialogTitle}>
            Stop Deduction Transaction
          </DialogTitle>
          <DialogContent className={styles.classes.stopDialogContent}>
            <FormControl fullWidth className={styles.classes.formControl}>
              <InputLabel id="stop-deduction-reason-label">Reason *</InputLabel>
              <Select
                labelId="stop-deduction-reason-label"
                label="Reason *"
                value={selectedStopReasonLabel}
                MenuProps={darkSelectMenuProps}
                onChange={(event) => {
                  setSelectedStopReasonLabel(event.target.value as string);
                  if (showStopReasonError) {
                    setShowStopReasonError(false);
                  }
                  if (showAdditionalCommentsError) {
                    setShowAdditionalCommentsError(false);
                  }
                }}
              >
                {stopReasonOptions.map((option) => (
                  <MenuItem key={option} value={option} className={styles.classes.menuItem}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showStopReasonError && (
              <Typography className={styles.classes.helperText}>Reason is required.</Typography>
            )}

            <TextField
              label="Additional comments"
              placeholder={selectedStopReasonLabel === 'Other' ? 'Required' : 'Optional'}
              multiline
              minRows={3}
              value={additionalComments}
              onChange={(event) => {
                setAdditionalComments(event.target.value);
                if (showAdditionalCommentsError && event.target.value.trim()) {
                  setShowAdditionalCommentsError(false);
                }
              }}
              className={styles.classes.formControl}
              fullWidth
            />

            {showAdditionalCommentsError && (
              <Typography className={styles.classes.helperText}>
                Additional comments are required when selecting &quot;Other&quot;.
              </Typography>
            )}
          </DialogContent>
          <DialogActions className={styles.classes.stopDialogActions}>
            <Button
              variant="outlined"
              className={styles.classes.secondaryButton}
              onClick={handleCloseStopDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={styles.classes.primaryButton}
              onClick={handleSubmitStopTransaction}
            >
              Stop Transaction
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isRetryOptionsDialogOpen}
          onClose={handleCloseRetryOptionsDialog}
          className={styles.classes.stopDialogContainer}
          classes={{ paper: styles.classes.retryDialogPaper }}
        >
          <DialogTitle className={styles.classes.stopDialogTitle}>Retry Vendor Payment</DialogTitle>
          <DialogContent className={styles.classes.retryDialogContent}>
            {retryDialogMode === 'options' ? (
              <>
                <Typography color="white" fontFamily="Inter" fontSize="14px">
                  Choose how you want to retry this vendor payment.
                </Typography>
                {isRetryOverrideLoading && (
                  <Box display="flex" alignItems="center" gap={1} py={1}>
                    <CircularProgress size={18} />
                    <Typography color="white" fontFamily="Inter" fontSize="13px">
                      Loading payment options...
                    </Typography>
                  </Box>
                )}
                {retryOverrideError && (
                  <Typography className={styles.classes.helperText}>{retryOverrideError}</Typography>
                )}
              </>
            ) : (
              <>
                <FormControl fullWidth className={styles.classes.formControl}>
                  <InputLabel id="retry-vendor-method-label">Payment Method *</InputLabel>
                  <Select
                    labelId="retry-vendor-method-label"
                    label="Payment Method *"
                    value={retryPaymentMethod}
                    MenuProps={darkSelectMenuProps}
                    onChange={(event) => {
                      setRetryPaymentMethod(event.target.value as string);
                      setRetryOverrideError('');
                    }}
                  >
                    {retryMethodOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id} className={styles.classes.menuItem}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {(retryPaymentMethod === 'ACH' || retryPaymentMethod === 'CHECK') && (
                  <FormControl fullWidth className={styles.classes.formControl}>
                    <InputLabel id="retry-vendor-account-label">Payment Account *</InputLabel>
                    <Select
                      labelId="retry-vendor-account-label"
                      label="Payment Account *"
                      value={retryPaymentAccountId}
                      MenuProps={darkSelectMenuProps}
                      onChange={(event) => {
                        setRetryPaymentAccountId(event.target.value as string);
                        setRetryOverrideError('');
                      }}
                    >
                      {retryAccountOptions.map((option) => (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          className={styles.classes.menuItem}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {retryOverrideError && (
                  <Typography className={styles.classes.helperText}>{retryOverrideError}</Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions className={styles.classes.retryDialogActions}>
            <Button
              variant="outlined"
              className={styles.classes.secondaryButton}
              onClick={handleCloseRetryOptionsDialog}
            >
              Cancel
            </Button>
            <Button
              variant="outlined"
              className={styles.classes.secondaryButton}
              onClick={handleRetrySameData}
              disabled={isRetryOverrideLoading}
            >
              Retry With Same Option
            </Button>
            {retryDialogMode === 'options' ? (
              <Button
                variant="contained"
                className={styles.classes.primaryButton}
                onClick={handleRetryOtherMethod}
                disabled={isRetryOverrideLoading}
              >
                Retry With Different Option
              </Button>
            ) : (
              <Button
                variant="contained"
                className={styles.classes.primaryButton}
                disabled={isRetryOverrideLoading}
                onClick={handleRetryWithUpdatedData}
              >
                Retry Payment
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default DetailsDeductionTable;
